import React, { useEffect, useState } from 'react';
import {
  loadKeysByMonthByWorkflow,
  exportKeysByMonthByWorkflow,
} from '../../../../actions/reportByMonthActions';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../../common/Table';
import { CommaNumber_NoDecimal, capitalizeFirstLetter } from '../../../../util/formatter';
import Loading from '../../../common/Loading';
import KwPanel from '../../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../../hooks/useTableSettings';
import { useReportYear } from '../../../../hooks/useReportYear';
import YearFormSelect from '../YearFormSelect';
import CompanyFormSelect from '../CompanyFormSelect';
import WorkflowLink from '../../../common/links/WorkflowLink';
import { useTranslation } from 'react-i18next';

import { scopes } from '../../../../constants/scopes';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchCompanies } from '../../../../reducers/companyReducer';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import type { ListApiResponse } from '../../../../types/apiResponse';
import type { CompanyOption } from '../CompanyFormSelect';

export interface KeysByMonthByWorkflowListProps { tableViewSwitcher?: React.ReactNode }
function KeysByMonthByWorkflowList({ tableViewSwitcher }: KeysByMonthByWorkflowListProps) {
  const companies = useAppSelector((state) => state.companies);
  const currentUser = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { year, years, makeHandleYearChange } = useReportYear();
  const months = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ];
  const allColumns = ['workflow', 'year'];
  for (const month of months) {
    allColumns.push(month);
  }
  const defaultVisibleColumns = allColumns;
  const [reload, setReload] = useState<Date | undefined>(undefined);
  const [items, setItems] = useState<ListApiResponse<unknown> | undefined>(undefined);
  const [company, setCompany] = useState<string>('');
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
    parameters,
  } = useTableSettings(
    'keysbymonthreport-workflow-settings',
    getTableSettingsDefaults({
      defaultSort: 'workflow',
      defaultVisibleColumns,
    }),
    { year, company },
    loadKeysByMonthByWorkflow,
    setItems,
    reload,
    t('Failed to load report')
  );

  useEffect(() => {
    if (!companies) {
      dispatch(fetchCompanies());

      const params = new URLSearchParams(location.search);
      const companyId = params.get(scopes.COMPANY);
      if (companyId) {
        setCompany(companyId);
      }
    }
  }, [companies, dispatch, location.search]);

  // The base config of the columns, this will be filtered based on the visible (above)
  let columnsConfig: TableColumn[] = [
    {
      Key: 'workflow',
      Label: t('Workflow'),
      Render: (item: Record<string, unknown>) => (
        <WorkflowLink
          id={(item as { keyWorkflowId?: string }).keyWorkflowId as string}
          name={(item as { workflow?: string }).workflow}
          colour={(item as { colourHex?: string }).colourHex}
        />
      ),
      Filter: {
        Render: (col: TableColumn) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'year',
      Label: t('Year'),
      Render: (item: Record<string, unknown>) => item.year as React.ReactNode,
    },
  ];
  // Add the months to the array
  for (const month of months) {
    columnsConfig.push({
      Key: month,
      Label: t(capitalizeFirstLetter(month)),
      className: 'text-right num no-wrap',
      Render: (row: Record<string, unknown>) => CommaNumber_NoDecimal(Number(row[month])),
    } as TableColumn);
  }

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);
  const handleYearChange = makeHandleYearChange(parameters, setReload);

  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const params = new URLSearchParams(location.search);
    if (e.target.value === '') {
      params.delete(scopes.COMPANY);
    } else {
      params.set(scopes.COMPANY, e.target.value);
    }
    setCompany(e.target.value);
    parameters.current = { ...parameters.current, company: e.target.value };
    setReload(new Date());
    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true,
    });
  };

  // return an alaska table
  return items ? (
    <>
      <div className="form-horizontal row px-4 px-md-0">
        <div className="col-md-6">
          <YearFormSelect
            year={year}
            years={years}
            handleYearChange={handleYearChange}
          ></YearFormSelect>
        </div>
        <div className="col-md-6">
          <CompanyFormSelect
            company={company}
            companies={companies as CompanyOption[] | null | undefined}
            handleCompanyChange={handleCompanyChange}
          ></CompanyFormSelect>
        </div>
      </div>
      <KwPanel className="mb-4">
        <Table
          className="table-v-stripes"
          columns={columns}
          allColumns={allColumns}
          defaultVisibleColumns={defaultVisibleColumns}
          rows={items.data.map((x: any) => ({
            Key: (x as { skuId?: unknown }).skuId,
            ...x,
          }))}
          totalRowCount={items.totalRowCount}
          filteredRowCount={items.filteredRowCount}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          isLoading={isLoading}
          filters={filters}
          filterChange={filterChange}
          exportData={(filters, scopes) =>
            exportKeysByMonthByWorkflow(year, company, (filters ?? {}) as Record<string, string>, (scopes ?? {}) as Record<string, string>)
          }
          tableActionBarButtons={tableViewSwitcher}
        />
      </KwPanel>
    </>
  ) : (
    <Loading></Loading>
  );
}

export default KeysByMonthByWorkflowList;
